import React, { useEffect, useMemo, useState } from "react";
import { api } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { toast } from "sonner";
import { AlertCircle, Plus } from "lucide-react";

/**
 * Dialog pentru adăugarea ECHIPAMENTELOR (non-PSA) cu numerotare prefixată.
 * Categorie = equipment_category (text liber sau din lista existentă).
 * Model cu prefix de 1-2 litere → numerotare globală pe (categorie + model).
 * Ex: airless / Graco → G1, G2; aspirator / Kärcher → K1, K2.
 *
 * Toggle „Retroactiv (fără prefix)" pentru echipamente cumpărate anterior — folosește
 * un starting_serial liber (ex: "27" sau "BOSCH-2017-05") cu auto-increment numeric.
 */

const LOCATIONS_FALLBACK = ["MAGAZIE", "PAXLI", "ACASA VICTOR"];
const COLOR_OPTIONS = ["", "Roșu", "Galben", "Albastru", "Verde", "Gri", "Mov"];

export default function EquipmentBatchDialog({ defaultCategory, meta, onClose, onSaved }) {
  const [category, setCategory] = useState((defaultCategory || "").trim());
  const [models, setModels] = useState([]);
  const [model, setModel] = useState("");
  const [size, setSize] = useState("");
  const [count, setCount] = useState(1);
  const [location, setLocation] = useState("MAGAZIE");
  const [color, setColor] = useState("");
  const [supplier, setSupplier] = useState("");
  const [price, setPrice] = useState("");
  const [purchaseDate, setPurchaseDate] = useState("");
  const [note, setNote] = useState("");
  const [noPrefix, setNoPrefix] = useState(false);
  const [startingSerial, setStartingSerial] = useState("");
  const [preview, setPreview] = useState(null);
  const [saving, setSaving] = useState(false);
  const [addModelOpen, setAddModelOpen] = useState(false);
  const [existingCategories, setExistingCategories] = useState([]);

  const locOptions = meta?.locations?.length ? meta.locations : LOCATIONS_FALLBACK;

  // Sugerează categoriile deja folosite (atât din meta.equipment_categories cât și din registry)
  useEffect(() => {
    const fromMeta = meta?.equipment_categories || [];
    api.get("/inventory/model-registry", { params: { item_type: "echipament" } })
      .then((r) => {
        const cats = Array.from(new Set([...fromMeta, ...((r.data || []).map((m) => m.category))]
          .filter((c) => c && c.trim()))).sort();
        setExistingCategories(cats);
      })
      .catch(() => setExistingCategories([...fromMeta].sort()));
  }, [meta]);

  const normalizedCat = (category || "").trim().toLowerCase();

  const loadModels = async () => {
    if (!normalizedCat) { setModels([]); setModel(""); return; }
    try {
      const r = await api.get("/inventory/model-registry", {
        params: { item_type: "echipament", category: normalizedCat },
      });
      const list = r.data || [];
      setModels(list);
      if (list.length > 0 && !list.find((m) => m.model === model)) {
        setModel(list[0].model);
      } else if (list.length === 0) {
        setModel("");
      }
    } catch {
      setModels([]);
    }
  };
  useEffect(() => { loadModels(); /* eslint-disable-next-line */ }, [normalizedCat]);

  useEffect(() => {
    if (noPrefix || !model || !normalizedCat || count < 1) { setPreview(null); return; }
    let cancelled = false;
    api.get("/inventory/model-registry/preview", {
      params: { item_type: "echipament", category: normalizedCat, model, count },
    })
      .then((r) => { if (!cancelled) setPreview(r.data); })
      .catch(() => { if (!cancelled) setPreview(null); });
    return () => { cancelled = true; };
  }, [normalizedCat, model, count, noPrefix]);

  const retroactivePreview = useMemo(() => {
    if (!noPrefix || !startingSerial || count < 1) return null;
    const m = String(startingSerial).match(/^(.*?)(\d+)$/);
    if (!m) return count === 1 ? [startingSerial] : null;
    const head = m[1] ?? "";
    const start = parseInt(m[2], 10);
    const width = m[2].length;
    return Array.from({ length: count }, (_, i) => `${head}${String(start + i).padStart(width, "0")}`);
  }, [noPrefix, startingSerial, count]);

  const submit = async () => {
    if (!normalizedCat) { toast.error("Categoria este obligatorie"); return; }
    if (!model && !noPrefix) { toast.error("Modelul este obligatoriu"); return; }
    if (count < 1) { toast.error("Cantitatea trebuie cel puțin 1"); return; }
    if (noPrefix && !startingSerial.trim()) { toast.error("Pentru retroactiv: tastează primul nr. de serie"); return; }
    setSaving(true);
    try {
      const payload = {
        item_type: "echipament",
        category: normalizedCat,
        model: noPrefix ? (model || "—") : model,
        size: size.trim() || null,
        count: parseInt(count) || 1,
        location: location || "MAGAZIE",
        color: color || null,
        supplier: supplier.trim() || null,
        price_chf: price === "" ? null : parseFloat(price),
        purchase_date: purchaseDate || null,
        note: note.trim() || null,
        no_prefix: noPrefix,
        starting_serial: noPrefix ? startingSerial.trim() : null,
      };
      const r = await api.post("/inventory/batch-create", payload);
      const serials = r.data?.serials || [];
      toast.success(`${serials.length} echipament${serials.length === 1 ? "" : "e"} adăugate: ${serials.join(", ")}`);
      onSaved && onSaved();
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Eroare la adăugare");
    } finally { setSaving(false); }
  };

  const previewLabels = noPrefix ? retroactivePreview : (preview?.labels || null);

  return (
    <>
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto" data-testid="equipment-batch-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            🔧 Adaugă Echipamente — numerotare automată
          </DialogTitle>
          <p className="text-[12px] text-zinc-500">
            Categorie liberă + model cu prefix (1-2 litere). Sistemul numerotează automat <strong className="font-mono">G1, G2…</strong> per (categorie + model).
          </p>
        </DialogHeader>

        <div className="space-y-4">
          {/* 1. Categorie */}
          <div>
            <Label className="small-label">1. Categorie echipament *</Label>
            <div className="flex gap-2 mt-1">
              <Input
                value={category}
                onChange={(e) => setCategory(e.target.value)}
                placeholder="ex: airless, aspirator, masina_slefuit, messschreiber..."
                className="flex-1"
                list="eq-cat-suggestions"
                data-testid="eq-batch-category"
              />
              <datalist id="eq-cat-suggestions">
                {existingCategories.map((c) => <option key={c} value={c}/>)}
              </datalist>
            </div>
            {existingCategories.length > 0 && (
              <div className="mt-1.5 flex flex-wrap gap-1">
                {existingCategories.slice(0, 12).map((c) => (
                  <button
                    key={c}
                    type="button"
                    onClick={() => setCategory(c)}
                    className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded border ${normalizedCat === c.toLowerCase() ? "bg-zinc-800 text-white border-zinc-800" : "bg-white border-zinc-300 hover:bg-zinc-50"}`}
                    data-testid={`eq-batch-cat-quick-${c}`}
                  >
                    {c}
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Toggle retroactiv */}
          <div className="card-flat p-2 bg-zinc-50 flex items-center gap-3 text-sm">
            <label className="inline-flex items-center gap-2 cursor-pointer" data-testid="eq-batch-no-prefix-toggle">
              <input type="checkbox" checked={noPrefix} onChange={(e) => setNoPrefix(e.target.checked)} className="rounded"/>
              <span className="font-semibold">Retroactiv (fără prefix automat)</span>
            </label>
            <span className="text-[11px] text-zinc-500">Pentru echipamente cumpărate anterior, cu numerotare existentă.</span>
          </div>

          {/* 2. Model */}
          {!noPrefix && normalizedCat && (
            <div>
              <div className="flex items-center justify-between">
                <Label className="small-label">2. Model *</Label>
                <button
                  type="button"
                  onClick={() => setAddModelOpen(true)}
                  className="text-[11px] font-bold text-blue-700 hover:underline"
                  data-testid="eq-batch-add-model-btn"
                >
                  <Plus size={11} className="inline mr-0.5"/> Adaugă model nou
                </button>
              </div>
              <Select value={model} onValueChange={setModel}>
                <SelectTrigger className="mt-1" data-testid="eq-batch-model">
                  <SelectValue placeholder={models.length === 0 ? "Niciun model — apasă + Adaugă model nou" : "Selectează model"} />
                </SelectTrigger>
                <SelectContent>
                  {models.length === 0 && <div className="px-3 py-2 text-xs text-zinc-500 italic">Niciun model — adaugă unul.</div>}
                  {models.map((m) => (
                    <SelectItem key={m.id || `${m.model}-${m.prefix}`} value={m.model}>
                      <span className="font-mono font-bold mr-1.5">{m.prefix}</span> {m.model} <span className="text-[10px] text-zinc-500 ml-1">· ultim #{m.last_number}</span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          {/* 3. Mărime / variantă opțional */}
          <div>
            <Label className="small-label">3. Mărime / variantă (opțional)</Label>
            <Input
              value={size}
              onChange={(e) => setSize(e.target.value)}
              placeholder="ex: 18V, 7.5kW, 220V — sau lasă gol"
              className="mt-1"
              data-testid="eq-batch-size"
            />
          </div>

          {/* 4. Cantitate */}
          <div>
            <Label className="small-label">4. Câte bucăți? *</Label>
            <Input
              type="number"
              min={1}
              max={200}
              value={count}
              onChange={(e) => setCount(parseInt(e.target.value) || 1)}
              className="mt-1 max-w-[160px]"
              data-testid="eq-batch-count"
            />
          </div>

          {/* Starting serial pt retroactiv */}
          {noPrefix && (
            <div className="card-flat p-3 bg-amber-50/40 border-l-4 border-l-amber-400">
              <Label className="small-label">Primul număr de serie (retroactiv) *</Label>
              <Input
                value={startingSerial}
                onChange={(e) => setStartingSerial(e.target.value)}
                placeholder="ex: 27, 100, BOSCH-001"
                className="mt-1"
                data-testid="eq-batch-starting-serial"
              />
              <p className="text-[11px] text-zinc-600 mt-1.5 italic">
                Următoarele se incrementează automat (ex: BOSCH-001 → 001, 002, 003).
              </p>
            </div>
          )}

          {/* PREVIEW */}
          {previewLabels && previewLabels.length > 0 && (
            <div className="card-flat p-3 bg-emerald-50 border-l-4 border-l-emerald-500" data-testid="eq-batch-preview">
              <div className="text-[10px] uppercase tracking-wider font-bold text-emerald-800 mb-1.5">
                Se vor crea {previewLabels.length} {previewLabels.length === 1 ? "bucată" : "bucăți"}
              </div>
              <div className="flex flex-wrap gap-1.5">
                {previewLabels.map((s) => (
                  <span key={s} className="font-mono text-xs font-bold bg-white border-2 border-emerald-300 text-emerald-900 px-2 py-0.5 rounded">
                    {s}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Optional fields */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <Label className="small-label">Locație</Label>
              <Select value={location} onValueChange={setLocation}>
                <SelectTrigger className="mt-1" data-testid="eq-batch-location"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {locOptions.map((l) => <SelectItem key={l} value={l}>{l}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="small-label">Culoare echipă (opțional)</Label>
              <Select value={color || "none"} onValueChange={(v) => setColor(v === "none" ? "" : v)}>
                <SelectTrigger className="mt-1" data-testid="eq-batch-color"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">— fără culoare —</SelectItem>
                  {COLOR_OPTIONS.filter(Boolean).map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="small-label">Furnizor (opțional)</Label>
              <Input value={supplier} onChange={(e) => setSupplier(e.target.value)} placeholder="ex: Hilti Switzerland" className="mt-1" data-testid="eq-batch-supplier"/>
            </div>
            <div>
              <Label className="small-label">Preț unitar CHF</Label>
              <Input type="number" step="0.01" value={price} onChange={(e) => setPrice(e.target.value)} placeholder="ex: 1450.00" className="mt-1" data-testid="eq-batch-price"/>
            </div>
            <div>
              <Label className="small-label">Data cumpărării (opțional)</Label>
              <Input type="date" value={purchaseDate} onChange={(e) => setPurchaseDate(e.target.value)} className="mt-1" data-testid="eq-batch-purchase-date"/>
            </div>
            <div className="sm:col-span-2">
              <Label className="small-label">Notă (opțional)</Label>
              <Textarea rows={2} value={note} onChange={(e) => setNote(e.target.value)} placeholder="ex: primite pe 01.02.2026" className="mt-1" data-testid="eq-batch-note"/>
            </div>
          </div>
        </div>

        <DialogFooter className="mt-4">
          <Button variant="outline" onClick={onClose} disabled={saving}>Anulează</Button>
          <Button onClick={submit} disabled={saving} className="bg-[var(--primary)]" data-testid="eq-batch-save">
            {saving ? "Se salvează…" : `Adaugă ${count} buc.`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>

    {addModelOpen && (
      <EquipmentAddModelDialog
        category={normalizedCat}
        onClose={() => setAddModelOpen(false)}
        onSaved={(newModel) => {
          setModel(newModel);
          loadModels();
          setAddModelOpen(false);
        }}
      />
    )}
    </>
  );
}

function EquipmentAddModelDialog({ category, onClose, onSaved }) {
  const [name, setName] = useState("");
  const [prefix, setPrefix] = useState("");
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    if (!category) { toast.error("Categoria lipsește"); return; }
    if (!name.trim()) { toast.error("Numele modelului obligatoriu"); return; }
    const p = prefix.trim().toUpperCase();
    if (!/^[A-Z]{1,2}$/.test(p)) { toast.error("Prefix-ul trebuie 1-2 litere (A-Z)"); return; }
    setSaving(true);
    try {
      await api.post("/inventory/model-registry", {
        item_type: "echipament", category, model: name.trim(), prefix: p,
      });
      toast.success(`Model „${name.trim()}" salvat cu prefix „${p}"`);
      onSaved(name.trim());
    } catch (e) { toast.error(e?.response?.data?.detail || "Eroare"); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent data-testid="eq-add-model-dialog">
        <DialogHeader>
          <DialogTitle>Model nou pentru categoria: {category}</DialogTitle>
        </DialogHeader>
        <div className="space-y-3 text-sm">
          <div className="flex gap-2 items-start">
            <AlertCircle size={16} className="text-blue-600 mt-1 shrink-0"/>
            <p className="text-[12px] text-zinc-600">
              Modelele sunt salvate global per (categorie + model). Prefix-ul determină numerotarea: <strong className="font-mono">G1, G2…</strong> pentru Graco, <strong className="font-mono">H1, H2…</strong> pentru Hilti.
            </p>
          </div>
          <div>
            <Label className="small-label">Numele modelului *</Label>
            <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="ex: Graco, Hilti, Kärcher, Bosch..." className="mt-1" data-testid="eq-new-model-name" autoFocus/>
          </div>
          <div>
            <Label className="small-label">Prefix (1-2 litere mari) *</Label>
            <Input
              value={prefix}
              onChange={(e) => setPrefix(e.target.value.toUpperCase().slice(0, 2))}
              placeholder="ex: G, HI, KR"
              className="mt-1 font-mono uppercase max-w-[120px]"
              maxLength={2}
              data-testid="eq-new-model-prefix"
            />
            {prefix && /^[A-Z]{1,2}$/.test(prefix) && (
              <p className="text-[11px] text-emerald-700 mt-1">
                Numerele vor arăta: <span className="font-mono font-bold">{prefix}1, {prefix}2, {prefix}3</span>...
              </p>
            )}
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={saving}>Anulează</Button>
          <Button onClick={submit} disabled={saving} className="bg-[var(--primary)]" data-testid="eq-new-model-save">Salvează</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
